import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";

import { bulkCreateLocations } from "api/locations";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

// Mirrors LOCATION_MIN_LEVEL / LOCATION_MAX_LEVEL / LOCATION_LEVEL_STEP on the backend.
// A level keeps its own width in the code, so both 102-002-10 and 102-002-100 are valid.
const LEVEL_STEP = 10;
const MIN_LEVEL = 10;
const MAX_LEVEL = 990;

const INITIAL_DATA = {
  aisle_from: "",
  aisle_to: "",
  bin_from: "",
  bin_to: "",
  level_from: "10",
  level_to: "90",
};

const isThreeDigits = (value) => /^\d{3}$/.test(value);

const isLevel = (value) =>
  /^\d{2,3}$/.test(value) &&
  !(Number(value) % LEVEL_STEP) &&
  Number(value) >= MIN_LEVEL &&
  Number(value) <= MAX_LEVEL;

// An empty "to" means a single value, so creating just aisle 501 / bin 002 doesn't
// require typing the same number twice. Mirrors generate_location_codes() on the backend.
const withRangeDefaults = (data) => ({
  ...data,
  aisle_to: data.aisle_to || data.aisle_from,
  bin_to: data.bin_to || data.bin_from,
  level_to: data.level_to || data.level_from,
});

// Same rules as generate_location_codes() on the backend, which stays authoritative.
// This only exists so the user gets the error without a round trip.
const validate = ({
  aisle_from,
  aisle_to,
  bin_from,
  bin_to,
  level_from,
  level_to,
}) => {
  if (!isThreeDigits(aisle_from))
    return "Aisle from must be exactly 3 digits (e.g. 002)";
  if (!isThreeDigits(aisle_to))
    return "Aisle to must be exactly 3 digits (e.g. 002)";
  if (!isThreeDigits(bin_from))
    return "Bin from must be exactly 3 digits (e.g. 002)";
  if (!isThreeDigits(bin_to))
    return "Bin to must be exactly 3 digits (e.g. 002)";
  if (Number(bin_from) % 2 || Number(bin_to) % 2)
    return "Bin must be even (e.g. 002, 004)";
  if (!isLevel(level_from))
    return `Level from must be a multiple of ${LEVEL_STEP} between ${MIN_LEVEL} and ${MAX_LEVEL} (e.g. 10, 90, 100)`;
  if (!isLevel(level_to))
    return `Level to must be a multiple of ${LEVEL_STEP} between ${MIN_LEVEL} and ${MAX_LEVEL} (e.g. 10, 90, 100)`;
  if (Number(aisle_from) > Number(aisle_to))
    return "Aisle from cannot be greater than aisle to";
  if (Number(bin_from) > Number(bin_to))
    return "Bin from cannot be greater than bin to";
  if (Number(level_from) > Number(level_to))
    return "Level from cannot be greater than level to";
  return null;
};

const countCombinations = (data) => {
  if (validate(data)) return 0;
  const aisles = Number(data.aisle_to) - Number(data.aisle_from) + 1;
  const bins = (Number(data.bin_to) - Number(data.bin_from)) / 2 + 1;
  const levels =
    (Number(data.level_to) - Number(data.level_from)) / LEVEL_STEP + 1;
  return aisles * bins * levels;
};

export default function LocationCreateModal({ onClose }) {
  const layoutDispatch = useLayoutDispatch();
  const [data, setData] = useState(INITIAL_DATA);
  const [isFetching, setIsFetching] = useState(false);

  // Digits only, max 3 - keeps "002" intact instead of letting it become 2.
  const handleDigits = (prop) => ({ target: { value } }) => {
    setData((prev) => ({
      ...prev,
      [prop]: value.replace(/\D/g, "").slice(0, 3),
    }));
  };

  const handleSubmit = () => {
    const payload = withRangeDefaults(data);
    const error = validate(payload);
    if (error) {
      return popupNotification({ dispatch: layoutDispatch, message: error });
    }

    setIsFetching(true);
    bulkCreateLocations({
      body: payload,
      responseSetter: (res) => {
        setIsFetching(false);
        const { success, message } = res;
        if (!success) {
          return popupNotification({ dispatch: layoutDispatch, message });
        }

        onClose(true);
        popupNotification({
          dispatch: layoutDispatch,
          message,
          status: "success",
        });
      },
    });
  };

  const total = countCombinations(withRangeDefaults(data));

  return (
    <Dialog
      open={true}
      onClose={() => !isFetching && onClose(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create Locations</DialogTitle>
      <DialogContent>
        {isFetching ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Aisle From"
                placeholder="002"
                variant="outlined"
                fullWidth
                value={data.aisle_from}
                onChange={handleDigits("aisle_from")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Aisle To"
                placeholder="same as from"
                variant="outlined"
                fullWidth
                value={data.aisle_to}
                onChange={handleDigits("aisle_to")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Bin From (even)"
                placeholder="002"
                variant="outlined"
                fullWidth
                value={data.bin_from}
                onChange={handleDigits("bin_from")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Bin To (even)"
                placeholder="same as from"
                variant="outlined"
                fullWidth
                value={data.bin_to}
                onChange={handleDigits("bin_to")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Level From (step 10)"
                placeholder="10"
                variant="outlined"
                fullWidth
                value={data.level_from}
                onChange={handleDigits("level_from")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Level To (step 10)"
                placeholder="same as from"
                variant="outlined"
                fullWidth
                value={data.level_to}
                onChange={handleDigits("level_to")}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {total > 0
                  ? `${total} location(s) in this range. Existing ones are skipped.`
                  : "Codes are created as <aisle>-<bin>-<level>, e.g. 513-002-10."}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onClose(false)}
          disabled={isFetching}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isFetching || total === 0}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
